import { Button } from "../../../components/ui/button";
import {
  FaPlus,
  FaEye,
  FaPencilAlt,
  FaCheckCircle,
  FaTrashAlt,
  FaTrash,
} from "react-icons/fa";
import { FiFilter, FiSearch } from "react-icons/fi";
import { Input } from "../../../components/ui/input";
import { CombinedDataTableTreeMenu } from "../../../components/CombinedDataTableTreeMenu";
import { useEffect, useState } from "react";
import { Label } from "../../../components/ui/label";
import Modal from "../../../components/Modal";
import { dashboardService, materialService } from "../../../services";
import { Skeleton } from "../../../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import TableCard from "../../../components/TableCard";
import ShortenedText from "../../../components/ShortenedText";
import { LucideRecycle } from "lucide-react";
import LoadingImage from "../../../components/LoadingImage";
import { createItemStore } from "../../../stores/createItemStore";
import { toast } from "react-toastify";
import { GoCheckCircle, GoCheckCircleFill } from "react-icons/go";

// ========================================================
// Step 1: Zustand store instantiation
// ========================================================
const dataMap = (item) => item;

const storeName = "materials";
const useItems = createItemStore(
  storeName,
  (material) => material._id,
  materialService.getMaterial,
  dataMap
);
// ========================================================

const MaterialManagement = () => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isActivateOpen, setIsActivateOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedMaterialId, setSelectedMaterialId] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    unit: "",
    pointsPerUnit: "",
  });

  const [editFormData, setEditFormData] = useState({
    id: "",
    category: "",
    description: "",
    unit: "",
    pointsPerUnit: "",
  });

  useEffect(() => {
    if (selectedMaterial) {
      let trimmedId = selectedMaterial.id;

      // Extract the last 4 sections but exclude the last one
      if (trimmedId) {
        const idParts = trimmedId.split("-");
        if (idParts.length >= 4) {
          trimmedId = idParts.slice(-4, -1).join("-"); // Take last 4, remove the last one
        }
      }

      setEditFormData({
        id: trimmedId || "", // Use trimmed ID
        category: selectedMaterial.category || "",
        description: selectedMaterial.description || "",
        unit: selectedMaterial.unit || "",
        pointsPerUnit: selectedMaterial.point || "",
      });
    }
  }, [selectedMaterial]);

  // Update form data when input changes
  const handleInputChange = (e) => {
    setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
  };

  // Handle edit material submission
  const handleSaveChanges = async () => {
    try {
      const payload = {
        MaterialId: editFormData.id, // Ensure this is trimmed if needed
        Category: editFormData.category,
        Description: editFormData.description,
        Unit: editFormData.unit,
        Point: Number(editFormData.pointsPerUnit), // Ensure it's a number
      };

      console.log("Submitting Edited Material Data:", payload);

      await materialService.editMaterialDetails(payload);
      fetchItems();
      // await fetchMaterials();
      toast.success("🎉 Material updated successfully!", {
        autoClose: 2000,
      });
      setIsEditOpen(false);
    } catch (error) {
      console.error("Error updating material:", error);
      toast.error("❌ Failed to update material", {
        autoClose: 2000,
      });
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle unit selection
  const handleUnitChange = (value) => {
    setFormData({ ...formData, unit: value });
  };

  // Handle add material
  const handleSubmit = async () => {
    if (!formData.name || !formData.unit || !formData.pointsPerUnit) {
      alert("Please fill in all required fields.");
      return;
    }

    setModalLoading(true); // Start loading for the modal submission

    // Extract the last 4 sections of the ID but exclude the final section
    let trimmedId = selectedMaterialId;
    if (selectedMaterialId) {
      const idParts = selectedMaterialId.split("-");
      if (idParts.length >= 4) {
        trimmedId = idParts.slice(-4, -1).join("-"); // Take the last 4 but remove the last one
      }
    }

    const payload = {
      name: formData.name,
      description: formData.description,
      unit: formData.unit,
      pointsPerUnit: formData.pointsPerUnit,
      subMaterialId: trimmedId, // Send the trimmed ID
    };

    try {
      await materialService.addMaterial(payload); // Add the new material
      toast.success("🎉 Material added successfully!", {
        autoClose: 2000,
      });

      // Refresh the materials list after adding a new material
      fetchItems();
      // await fetchMaterials();

      setIsAddOpen(false);
      setFormData({ name: "", description: "", unit: "", pointsPerUnit: "" });
      setSelectedMaterialId(null); // Reset after submission
    } catch (error) {
      console.error("Failed to add material:", error);
      toast.error("❌ Failed to add material", {
        autoClose: 2000,
      });
    } finally {
      setModalLoading(false); // Stop loading for modal
    }
  };

  // Activate material
  const handleActivateMaterial = async () => {
    if (!selectedMaterial) return;

    try {
      setLoading(true);

      const payload = {
        MaterialId: editFormData.id,
        Category: selectedMaterial.category,
        Description: selectedMaterial.description,
        Unit: selectedMaterial.unit,
        Point: Number(selectedMaterial.pointsPerUnit),
        Status: "A",
      };

      console.log("Activating material:", payload);

      await materialService.editMaterialDetails(payload);
      fetchItems();
      // await fetchMaterials();

      setIsActivateOpen(false);
      setSelectedMaterialId(null); // Optional: if still used
      toast.success("🎉 Material activated successfully!", {
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error activating material:", error);
      toast.error("❌ Failed to activate material", {
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  // deactivate material
  const handleDeleteMaterial = async () => {
    if (!selectedMaterial) return;

    try {
      setLoading(true); // Show loading spinner

      const payload = {
        MaterialId: editFormData.id,
        Category: selectedMaterial.category,
        Description: selectedMaterial.description,
        Unit: selectedMaterial.unit,
        Point: Number(selectedMaterial.pointsPerUnit),
        Status: "D", // Soft delete
      };

      console.log("Soft-deleting material:", payload);

      await materialService.editMaterialDetails(payload);
      fetchItems();
      // await fetchMaterials();

      setIsDeleteOpen(false);
      setSelectedMaterialId(null); // Optional: if still used
      toast.success("🎉 Material deactivated successfully!", {
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error deactivating material:", error);
      toast.error("❌ Failed to deactivate material", {
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  const actions = (row) => {
    const baseActions = [
      {
        label: "Add Material",
        icon: <FaCheckCircle />,
        onClick: () => {
          setSelectedMaterialId(row.id);
          setIsAddOpen(true);
        },
      },
      {
        label: "View Material",
        icon: <FaEye />,
        onClick: () => {
          setSelectedMaterial(row);
          setIsViewOpen(true);
        },
      },
      {
        label: "Edit Material",
        icon: <FaPencilAlt />,
        onClick: () => {
          setSelectedMaterial(row);
          setIsEditOpen(true);
        },
      },
    ];

    // Add the appropriate status toggle action based on current status
    const statusAction =
      row.status === "D"
        ? {
            label: "Activate Material",
            variant: "dropGreen",
            icon: <GoCheckCircleFill />,
            onClick: () => {
              setSelectedMaterial(row);
              setIsActivateOpen(true);
            },
          }
        : {
            label: "Deactivate Material",
            variant: "dropRed",
            icon: <FaTrashAlt />,
            onClick: () => {
              setSelectedMaterial(row);
              setIsDeleteOpen(true);
            },
          };

    return [...baseActions, statusAction];
  };

  // Table columns
  const tableColumns = [
    { header: "Category", accessorKey: "category" },
    { header: "Description", accessorKey: "description" },
    { header: "Unit", accessorKey: "unit" },
    { header: "Point Per Unit", accessorKey: "point" },
  ];

  // ========================================================
  // Step 2: Zustand store and page hydration
  // ========================================================
  const { isLoading, fetchItems, getSortedFiltered } = useItems(
    (state) => state
  );
  const data = getSortedFiltered(null, (a, b) => a.category.localeCompare(b.category));
  const formattedData = materialService.getTreeData(data);
  // console.log(`MaterialManagement formattedData:`, formattedData);

  const showLoading = data.length === 0 && isLoading;
  useEffect(() => {
    fetchItems();
  }, []);
  // ========================================================

  const [recycledStats, setRecycledStats] = useState({
    totalPoints: 0,
    trend: { percentage: "0%", isUp: false },
  });

  useEffect(() => {
    const fetchRecycledStats = async () => {
      try {
        const response = await dashboardService.countRecycledMaterials();

        const totalPoints = response.total_points || 0;

        setRecycledStats({
          totalPoints,
          trend: {
            percentage: "20%",
            isUp: true,
          },
        });
      } catch (error) {
        console.error("Error fetching recycled materials stats:", error);
        setRecycledStats({
          totalPoints: 0,
          trend: { percentage: "0%", isUp: false },
        });
      }
    };

    fetchRecycledStats();
  }, []);

  useEffect(() => {
    console.log("Formatted data with status:", formattedData);
  }, [formattedData]);

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <div className="flex-1 flex flex-col">
        <div className="flex-1 p-4 sm:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Total Recycled points - With Skeleton */}
            {showLoading ? (
              <Skeleton className="w-full h-32 rounded-xl" />
            ) : (
              <TableCard
                heading="Total Recycled points"
                number={recycledStats.totalPoints}
                bgColor="from-fuchsia-50 to-fuchsia-500"
                icon={LucideRecycle}
                iconColor="text-fuchsia-500"
                borderColor="border-l-fuchsia-500"
              />
            )}
          </div>

          <div className="bg-white shadow rounded-lg p-4 sm:p-6 text-sm overflow-x-auto">
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-4">
              <div className="text-base sm:text-lg font-semibold text-[#111827] mb-4 sm:mb-0">
                Material List
              </div>
              <div className="flex space-x-4">
                <Button
                  onClick={() => setIsAddOpen(true)}
                  className="flex items-center px-4 py-2 bg-[#14B8A6] text-sm sm:text-base text-white rounded hover:bg-[#2DD4BF]"
                >
                  <FaPlus className="mr-2" /> Create New
                </Button>
              </div>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-4">
              <div className="relative w-full sm:w-[24rem]">
                <Input
                  type="text"
                  placeholder="Search by material name, description, unit or points..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <FiSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
              </div>
              <div className="flex items-center space-x-4">
                {searchQuery && (
                  <Button
                    variant="ghost"
                    onClick={() => setSearchQuery("")}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    Clear
                  </Button>
                )}
                <Button variant={"secondary"}>
                  <FiFilter className="mr-2" /> Filters
                </Button>
              </div>
            </div>

            {showLoading ? (
              // {loading ? (
              <LoadingImage />
            ) : data.length > 0 ? (
              // ) : treeMenuData.length > 0 ? (
              <CombinedDataTableTreeMenu
                treeMenuData={formattedData}
                // treeMenuData={treeMenuData}
                tableColumns={tableColumns}
                tableActions={actions}
                searchQuery={searchQuery}
              />
            ) : (
              <div className="text-center text-gray-500 py-6">
                No requests found.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Material Modal */}
      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add New Material"
        description="Fill in the details for the new material."
        icon={<FaPlus className="text-green-500" />}
        iconColor="green"
        actions={
          <div className="flex flex-col-reverse sm:flex-row justify-between sm:justify-end sm:space-x-4 w-full">
            <Button
              variant="secondary"
              onClick={() => setIsAddOpen(false)}
              disabled={modalLoading}
              className="mt-2 sm:mt-0"
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={modalLoading}>
              {modalLoading ? "Adding..." : "Add Material"}
            </Button>
          </div>
        }
      >
        {/* Modal Content */}
        <div className="text-left px-4 sm:px-6 py-4">
          <div className="space-y-6">
            {/* Name */}
            <div>
              <Label>Name</Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter material name"
              />
            </div>

            {/* Description */}
            <div>
              <Label>Description</Label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter material description"
                className="w-full p-2 border rounded-md focus:border-b-2 border-gray-300 focus:outline-none focus:border-b-[#14B8A6] text-sm"
                rows={3}
              />
            </div>

            {/* Unit and Points */}
            <div className="flex flex-col sm:flex-row sm:space-x-4">
              {/* Unit */}
              <div className="w-full">
                <Label>
                  Unit{" "}
                  <span className="text-sm text-gray-500 font-normal ml-1">
                    (Measurement)
                  </span>
                </Label>
                <Select onValueChange={handleUnitChange}>
                  <SelectTrigger className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Kg">Kg</SelectItem>
                    <SelectItem value="g">g</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Points Per Unit */}
              <div className="w-full mt-4 sm:mt-0">
                <Label>Points Per Unit</Label>
                <Input
                  type="number"
                  name="pointsPerUnit"
                  value={formData.pointsPerUnit}
                  onChange={handleChange}
                  placeholder="Enter points per unit"
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* View Material Modal */}
      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Material Details"
        description="View the details of this material below."
        icon={<FaEye className="text-blue-500" />}
        iconColor="blue"
        actions={
          <div className="flex justify-between w-full">
            <Button variant={"secondary"} onClick={() => setIsViewOpen(false)}>
              Close
            </Button>
          </div>
        }
      >
        {/* Modal Content Wrapper */}
        <div className="max-h-[70vh] overflow-y-auto p-2">
          {/* Information Section */}
          <div className="space-y-6">
            {/* Material ID & Category */}
            <div className="flex justify-between items-center border-b pb-4">
              <div className="space-y-2">
                <Label className="text-lg font-semibold text-gray-700">
                  Material ID
                </Label>
                <span className="text-lg font-medium text-gray-900">
                  <ShortenedText
                    text={
                      selectedMaterial?.id
                        ? (() => {
                            const idParts = selectedMaterial.id.split("-");
                            return idParts.length >= 4
                              ? idParts.slice(-4, -1).join("-")
                              : selectedMaterial.id;
                          })()
                        : "N/A"
                    }
                  />
                </span>
              </div>
              <div className="space-y-2">
                <Label className="text-lg font-semibold text-gray-700">
                  Category
                </Label>
                <span className="text-lg font-medium text-gray-900">
                  {selectedMaterial?.category || "N/A"}
                </span>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-lg font-semibold text-gray-700">
                Description
              </Label>
              <span className="text-base text-gray-800">
                {selectedMaterial?.description || "No description available"}
              </span>
            </div>
          </div>

          <div className="border-t border-gray-300 my-6"></div>

          {/* Material Details Section */}
          <div className="space-y-4">
            {/* Name */}
            <div className="flex justify-between items-center">
              <Label className="text-lg font-semibold text-gray-700">
                Name
              </Label>
              <span className="text-gray-900 font-medium">
                {selectedMaterial?.category || "N/A"}
              </span>
            </div>

            {/* Unit */}
            <div className="flex justify-between items-center">
              <Label className="text-lg font-semibold text-gray-700">
                Unit
              </Label>
              <span className="text-gray-900 font-medium">
                {selectedMaterial?.unit || "N/A"}
              </span>
            </div>

            {/* Points Per Unit */}
            <div className="flex justify-between items-center">
              <Label className="text-lg font-semibold text-gray-700">
                Points Per Unit
              </Label>
              <span className="text-gray-900 font-medium">
                {selectedMaterial?.point || "N/A"}
              </span>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit Material Modal */}
      <Modal
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setSelectedMaterial(null);
          setEditFormData({
            id: "",
            category: "",
            description: "",
            unit: "",
            pointsPerUnit: "",
          });
        }}
        title="Edit Material"
        description="Modify the details for this material below."
        icon={<FaPencilAlt className="text-blue-500" />}
        iconColor="blue"
        actions={
          <div className="flex flex-col-reverse sm:flex-row justify-between sm:justify-end sm:space-x-4 w-full">
            <Button
              variant="secondary"
              onClick={() => setIsEditOpen(false)}
              className="mt-2 sm:mt-0"
            >
              Cancel
            </Button>
            <Button onClick={handleSaveChanges}>Save Changes</Button>
          </div>
        }
      >
        {/* Modal Content */}
        <div className="text-left px-4 sm:px-6 py-4">
          <div className="space-y-6">
            {/* Static Information */}
            <div className="space-y-2">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                <Label>Material ID:</Label>
                <span className="text-gray-700 break-words">
                  {editFormData.id}
                </span>
              </div>
            </div>

            <div className="border-t border-gray-300"></div>

            {/* Editable Fields */}
            <div className="space-y-4">
              {/* Name */}
              <div>
                <Label>Name</Label>
                <Input
                  type="text"
                  name="category"
                  value={editFormData.category}
                  onChange={handleInputChange}
                  placeholder="Enter material name"
                />
              </div>

              {/* Description */}
              <div>
                <Label>Description</Label>
                <textarea
                  name="description"
                  value={editFormData.description}
                  onChange={handleInputChange}
                  placeholder="Enter material description"
                  className="w-full p-2 border rounded-md focus:border-b-2 border-gray-300 focus:outline-none focus:border-b-[#14B8A6] text-sm"
                  rows={3}
                />
              </div>

              {/* Unit and Points */}
              <div className="flex flex-col sm:flex-row sm:space-x-4">
                {/* Unit */}
                <div className="w-full">
                  <Label>
                    Unit{" "}
                    <span className="text-sm text-gray-500 font-normal ml-1">
                      (Measurement)
                    </span>
                  </Label>
                  <Select
                    onValueChange={(value) =>
                      handleInputChange({ target: { name: "unit", value } })
                    }
                  >
                    <SelectTrigger className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500">
                      <SelectValue
                        placeholder={editFormData.unit || "Select unit"}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Kg">Kg</SelectItem>
                      <SelectItem value="g">g</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Points Per Unit */}
                <div className="w-full mt-4 sm:mt-0">
                  <Label>Points Per Unit</Label>
                  <Input
                    type="number"
                    name="pointsPerUnit"
                    value={editFormData.pointsPerUnit}
                    onChange={handleInputChange}
                    placeholder="Enter points per unit"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Activate Modal */}
      <Modal
        isOpen={isActivateOpen}
        onClose={() => setIsActivateOpen(false)}
        title="Activate Material"
        description="Are you sure you want to activate this material?"
        icon={<GoCheckCircleFill className="text-green-500" />}
        iconColor="green"
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setIsActivateOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleActivateMaterial} // Trigger the deletion
              disabled={loading} // Disable the button while loading
            >
              {loading ? "Activating" : "Activate"}
            </Button>
          </div>
        }
      ></Modal>

      {/* Deactivate Modal */}
      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Deactivate Material"
        description="Are you sure you want to deactivate this material?"
        icon={<FaTrash className="text-red-500" />}
        iconColor="red"
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setIsDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant={"destructive"}
              onClick={handleDeleteMaterial} // Trigger the deletion
              disabled={loading} // Disable the button while loading
            >
              {loading ? "Deactivating" : "Deactivate"}
            </Button>
          </div>
        }
      ></Modal>
    </div>
  );
};

export default MaterialManagement;
